import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";

import {
    ACard,
    ADataGrid,
    ADataGridFilter,
    AMultiSelectFilter,
    AMultiSelectOption,
    AMultiSelectOptionId,
    ASectionTitle,
    PagedSearchParams,
} from "@atiautomacao/ati-ui-library";

import {Box, Stack, TextField, TextFieldProps, Typography, useMediaQuery, useTheme} from "@mui/material";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {QueryClient, QueryClientProvider} from "react-query";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LoadingButton } from "@mui/lab";
import { SimCardDownloadOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";

import {differenceInDays, isValid} from "date-fns";
import { useSnackbar } from "notistack";
import dayjs, { Dayjs } from "dayjs";

import {DateFormat, LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {postAndDownloadFile} from "../../Reports/ManagementReport/utils/postAndDownloadFile";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {hideDashboardHeaderMenu} from "../../Dashboard/DashboardMenuReducer";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {appendPowerStationIds, AuditAlarmsQuery} from "./AuditAlarmsReactQuery";
import {useDebounce} from "../../../Utils/UseDebounce";
import {ActionConfig} from "../../Dashboard/Alarms/AlarmPage";
import {initColumnsData} from "./Columns";

import { AUTHORITIES, GATEWAY_URL, POWER_STATION_IDS_FILTER_STORAGE_KEY } from "../../../Config/Constants";
import { FindAllPowerStationsBasicReactQuery } from "../../Settings/PowerStation/PowerStationReactQuery";
import { usePersistedSelection } from "../../../Shared/Hooks/usePersistedSelection";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import {
    applyFiltersToColumns,
    buildDateFilterSanitizationMessage,
    buildDateFilterValidationMessage,
    mergePendingDateFilterInputValues,
    processAlarmDateFilters,
} from "../../../Utils/AlarmDateFilterValidationUtils";

export interface AlarmRow {
    id: string;
    selected?: boolean;
    teleObjectId: number;
    severityId: number;
    severityName: string;
    severityColor: string;
    description: string;
    macroRegionName: string;
    regionName: string;
    powerStationId: number;
    powerStationName: string | JSX.Element;
    skidId: number;
    skidName: string | JSX.Element;
    equipmentId: number;
    equipmentName: string;
    clearDate?: Date | string;
    acknowledgementDate?: Date | string;
    dateTime: Date | string;
    dateTimeNote: Date | string;
    acknowledgedUser: string;
    clearedUser: string;
    acknowledgementNote: string;
    descriptionNote: string;
    userNameNote: string;
    group: boolean | any;
    actionsConfig?: Array<ActionConfig>

}

const MAX_RANGE_DAYS = 60;

function AuditAlarms(){
    const theme = useTheme();
    const isMediumScreen = useMediaQuery('(max-width:1340px)');
    const { enqueueSnackbar } = useSnackbar();

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_AUDIT, AUTHORITIES.VIEW_AUDIT, AUTHORITIES.VIEW_AUDIT_ALARMS,
    ]);
    const isAuthorizedToSearchByExcel = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_AUDIT, AUTHORITIES.VIEW_AUDIT, AUTHORITIES.VIEW_AUDIT_ALARMS,
    ]);

    const dispatch = useAppDispatch();

    const handleStartDateTime = (newValue: Date) => {
        const firstDayOfMonth = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);
        return firstDayOfMonth;
    };
    const [startDateTime, setStartDateTime] = React.useState<Date>(handleStartDateTime(new Date()));
    const [endDateTime, setEndDateTime] = React.useState<Date>(new Date());
    const [startDateInput, setStartDateInput] = useState<Dayjs | null>(dayjs(startDateTime));
    const [endDateInput, setEndDateInput] = useState<Dayjs | null>(dayjs(endDateTime));
    const [dateRangeError, setDateRangeError] = useState<string | null>(null);
    const [dateRangeErrorField, setDateRangeErrorField] = useState<'start' | 'end' | null>(null);
    
    const { debouncedFn } = useDebounce(3000);
    const [debouncedDateRange, setDebouncedDateRange] = useState(() => ({
        start: startDateTime,
        end: endDateTime
    }));

    const [rows, setRows] = React.useState<AlarmRow[]>([]);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumnsData), 0, 50));
    // Clona initColumnsData: o ADataGrid altera column.filter por referência e poluiria a constante do módulo
    const [columns, setColumns] = useState(() => applyFiltersToColumns(initColumnsData, extractFiltersFromColumns(initColumnsData)));

    useEffect(() => {
        if (dateRangeError) return;
        debouncedFn(() => {
            setDebouncedDateRange({
                start: startDateTime,
                end: endDateTime
            });
        });
    }, [startDateTime, endDateTime, dateRangeError, debouncedFn]);

    const {data: powerStationsData, isSuccess: powerStationsLoaded, isError: powerStationsFailed} = FindAllPowerStationsBasicReactQuery();

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => (powerStationsData ?? []).map((ps) => ({id: ps.id, label: ps.name})),
        [powerStationsData]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((o) => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized: powerStationsInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    // Considera só ids existentes nas opções: com chave já gravada o hook informa isInitialized
    // antes de descartar ids que o usuário não possui mais
    const powerStationIdsForRequest = useMemo<number[] | null>(() => {
        if (!powerStationsInitialized) return null;
        const optionIds = new Set(powerStationOptionIds);
        const validIds = selectedPowerStationIds.filter((id) => optionIds.has(id));
        if (validIds.length === 0 || validIds.length === powerStationOptionIds.length) return null;
        return validIds.map((id) => Number(id));
    }, [selectedPowerStationIds, powerStationOptionIds, powerStationsInitialized]);

    // Usuário sem usinas nunca hidrata a seleção; consulta segue para exibir o estado vazio.
    // Falha na lista de usinas não bloqueia a auditoria: consulta segue sem restrição (backend limita às usinas do usuário)
    const isPowerStationSelectionReady = powerStationsFailed
        || (powerStationsLoaded && (powerStationsInitialized || powerStationOptions.length === 0));

    const handleApplyPowerStations = useCallback((ids: AMultiSelectOptionId[]) => {
        // Seleção vazia é ignorada: mantém a última seleção válida aplicada
        if (ids.length === 0) return;
        setSelectedPowerStationIds(ids);
        setPagedSearchParams((prev) => new PagedSearchParams(prev.filters, 0, prev.size));
    }, [setSelectedPowerStationIds]);

    const { data, isLoading, isSuccess } = AuditAlarmsQuery(
        pagedSearchParams,
        debouncedDateRange.start,
        debouncedDateRange.end,
        powerStationIdsForRequest,
        isPowerStationSelectionReady
    );

    // Enquanto a seleção de usinas não está pronta a consulta fica desabilitada (isLoading = false)
    const isAlarmsLoading = isLoading || !isPowerStationSelectionReady;
    const isExportDisabled = Boolean(dateRangeError) || !isSuccess || (data?.totalOfRecords ?? 0) === 0 || !isPowerStationSelectionReady;

 

    const [isDownloadCsv, setIsDownloadCsv] = useState(false);
    const [isDownloadExcel, setIsDownloadExcel] = useState(false);

    useEffect(() => {
        dispatch(hideDashboardHeaderMenu())
    }, [dispatch]);

    const updateRows = (data: any) => {
        const filteredData = data?.data?.map((alarm: any) => {
            return {
                teleObjectId: alarm.teleObjectId,
                selected:false,
                id: alarm.id.toString(),
                group: alarm.group,
                severityId: alarm.severityId,
                severityName: alarm.severityName,
                severityColor: alarm.severityColor,
                description: alarm.description,
                macroRegionName: alarm.macroRegionName,
                regionName: alarm.regionName,
                powerStationId: alarm.powerStationId,
                powerStationName: alarm.powerStationName,
                skidId: alarm.skidId,
                skidName: alarm.skidName,
                equipmentId: alarm.equipmentId,
                equipmentName: alarm.equipmentName,
                clearDate: alarm.clearDate ? DateFormat(alarm.clearDate) : "",
                acknowledgementDate: alarm.acknowledgementDate ? DateFormat(alarm.acknowledgementDate) : "",
                dateTime: DateFormat(alarm.dateTime),
                clearedUser: alarm.clearedUser,
                acknowledgedUser: alarm.acknowledgedUser,
                descriptionNote: alarm.descriptionNote,
                dateTimeNote: alarm.dateTimeNote ? DateFormat(alarm.dateTimeNote) : undefined,
                userNameNote: alarm.userNameNote,
                actionsConfig: [
                    {actionName: "grouping", disabled: false},
                    {actionName: "note", disabled: false},
                ],
            };
        }).sort((a:any, b:any) => {
            return new Date(b.dateTimeReq).getTime() - new Date(a.dateTimeReq).getTime();
        });

        setRows(filteredData ?? []);
    };

    useEffect(() => {
        if (isSuccess && data?.totalOfRecords !== undefined) {
            setTotalOfRecords(data?.totalOfRecords)
        }
    }, [data?.data]);

    useEffect(() => {
        updateRows(data);
    }, [data?.data]);

    const showDateFilterSanitizationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterSanitizationMessage(fieldLabels), {variant: 'warning'});
    }, [enqueueSnackbar]);

    const showDateFilterValidationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterValidationMessage(fieldLabels), {variant: 'error'});
    }, [enqueueSnackbar]);

    const getValidatedExportParams = (): PagedSearchParams | null => {
        if (dateRangeError || !isValid(debouncedDateRange.start) || !isValid(debouncedDateRange.end)) {
            enqueueSnackbar("Informe datas válidas no período.", {variant: 'error'});
            return null;
        }

        const currentFilters = mergePendingDateFilterInputValues(
            "alarm-audit-grid",
            extractFiltersFromColumns(columns),
            columns
        );
        const processedResult = processAlarmDateFilters(currentFilters);

        if (processedResult.wasSanitized) {
            setColumns(applyFiltersToColumns(initColumnsData, processedResult.filters));
            showDateFilterSanitizationMessage(processedResult.sanitizedFieldLabels);
            return null;
        }

        if (!processedResult.isValid) {
            showDateFilterValidationMessage(processedResult.invalidFieldLabels);
            return null;
        }

        const newPageParams = new PagedSearchParams(
            JSON.parse(JSON.stringify(processedResult.convertedFilters)),
            pagedSearchParams.page,
            pagedSearchParams.size
        );
        newPageParams.filters.push({name: "dateTime", condition: "starts-at", value: LocalDateTimeFormatISO(debouncedDateRange.start), sort: "none"});
        newPageParams.filters.push({name: "dateTime", condition: "ends-at", value: LocalDateTimeFormatISO(debouncedDateRange.end), sort: "none"});

        return newPageParams;
    };

    const getOnClickByType = (type: string) => {
        const validatedParams = getValidatedExportParams();
        if (!validatedParams) return;

        if (type === "excel") {
            setIsDownloadExcel(true);
            return handleDownloadExcel(validatedParams)
        }
        setIsDownloadCsv(true);
        return handleDownloadCSV(validatedParams)
        
    }

    const handleDownloadExcel = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/alarm/excel?`
        const exportParams = newPageParams.toURLSearchParams();
        appendPowerStationIds(exportParams, powerStationIdsForRequest);
        postAndDownloadFile(apiUrl, exportParams, 'auditoria_de_alarmes.xlsx',   
        () => {
            enqueueSnackbar("Sucesso ao baixar o Excel", {variant: 'success'});
            setIsDownloadExcel(false);
        },
        (error) => {
            enqueueSnackbar("Erro ao baixar o  Excel " + error, {variant: 'error'});
            setIsDownloadExcel(false);
        },
        () => {
            enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
            setIsDownloadExcel(false);
        });
    };

    const handleDownloadCSV = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/alarm/csv?`
        const exportParams = newPageParams.toURLSearchParams();
        appendPowerStationIds(exportParams, powerStationIdsForRequest);
        postAndDownloadFile(apiUrl, exportParams, 'auditoria_de_alarmes.csv',
        () => {
            enqueueSnackbar("Sucesso ao baixar o CSV", {variant: 'success'});
            setIsDownloadCsv(false);
        },
        (error) => {
            enqueueSnackbar("Erro ao baixar o  CSV " + error, {variant: 'error'});
            setIsDownloadCsv(false);
        },
        () => {
            enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
            setIsDownloadCsv(false);
        });
        
    };

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        const processedResult = processAlarmDateFilters(dataGridFilters);

        // Sincroniza as colunas apenas quando a sanitização alterou valores —
        // devolver novas colunas ao ADataGrid dispara onFiltersChange de novo e reseta a página (loop)
        if (processedResult.wasSanitized) {
            setColumns(applyFiltersToColumns(initColumnsData, processedResult.filters));
            showDateFilterSanitizationMessage(processedResult.sanitizedFieldLabels);
            return;
        }

        if (!processedResult.isValid) {
            showDateFilterValidationMessage(processedResult.invalidFieldLabels);
            return;
        }

        setPagedSearchParams(new PagedSearchParams(processedResult.convertedFilters, page, pageSize));
    }, [showDateFilterSanitizationMessage, showDateFilterValidationMessage]);

    function validateDateRange(
        start: Date | null,
        end: Date | null,
        changedField: 'start' | 'end',
        maxRangeDays: number = MAX_RANGE_DAYS,
    ): string | null {
        if (!start || !end || !isValid(start) || !isValid(end)) {
            return null;
        }

        const diffDays = differenceInDays(end, start);
        if (diffDays < 0) {
            return changedField === 'start'
                ? 'A data inicial não pode ser maior que a data final.'
                : 'A data final não pode ser menor que a data inicial.';
        }

        if (diffDays > maxRangeDays) {
            return `O período máximo permitido é de ${maxRangeDays} dias.`;
        }

        return null;
    }

    function resolveDateRangeError(
        startInput: Dayjs | null,
        endInput: Dayjs | null,
        changedField: 'start' | 'end',
    ): {error: string | null; field: 'start' | 'end' | null} {
        if (!startInput || !startInput.isValid()) {
            return {error: "Data inicial inválida.", field: 'start'};
        }
        if (!endInput || !endInput.isValid()) {
            return {error: "Data final inválida.", field: 'end'};
        }

        const error = validateDateRange(startInput.toDate(), endInput.toDate(), changedField);
        return {error, field: error ? changedField : null};
    }

    const handleRangeStartChange = (newValue: Dayjs | null) => {
        setStartDateInput(newValue);
        const {error, field} = resolveDateRangeError(newValue, endDateInput, 'start');

        if (!newValue || !newValue.isValid()) {
            setDateRangeError(error);
            setDateRangeErrorField(field);
            enqueueSnackbar("Informe uma data válida no campo Data inicial.", { variant: 'error' });
            return;
        }

        setStartDateTime(newValue.toDate());
        if (error) {
            setDateRangeError(error);
            setDateRangeErrorField(field);
            if (field === 'start') {
                enqueueSnackbar(error, { variant: 'warning' });
            }
            return;
        }

        setDateRangeError(null);
        setDateRangeErrorField(null);
    };

    const handleRangeEndChange = (newValue: Dayjs | null) => {
        setEndDateInput(newValue);
        const {error, field} = resolveDateRangeError(startDateInput, newValue, 'end');

        if (!newValue || !newValue.isValid()) {
            setDateRangeError(error);
            setDateRangeErrorField(field);
            enqueueSnackbar("Informe uma data válida no campo Data final.", { variant: 'error' });
            return;
        }

        setEndDateTime(newValue.toDate());
        if (error) {
            setDateRangeError(error);
            setDateRangeErrorField(field);
            if (field === 'end') {
                enqueueSnackbar(error, { variant: 'warning' });
            }
            return;
        }

        setDateRangeError(null);
        setDateRangeErrorField(null);
    };

    const dateHelperTextProps = (anchor: 'start' | 'end'): TextFieldProps['FormHelperTextProps'] => ({
        sx: {
            position: 'absolute',
            top: '100%',
            mx: 0,
            mt: 0.5,
            whiteSpace: 'nowrap',
            ...(anchor === 'end' ? {right: 0} : {}),
        },
    });
    const dateFieldSx = {
        width: '200px',
        position: 'relative',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: theme.palette.text.secondary,
            height: '40px',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.secondary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.error.main,
            },
        },
    };
    return (
        <>
            <Box sx={{flex: 1, minWidth: 0, mb: 0, marginLeft: 1}}>
                <ASectionTitle
                    titleLabel="Auditoria de Alarmes"
                    styleContainer={{flexDirection: isMediumScreen ? 'column' : 'row', padding: '0 0 40px 0', justifyContent: 'flex-start'}}
                    contentChildrenStyle={{justifyContent: isMediumScreen ? 'flex-start' : 'flex-end'}}
                    styleContainerTitle={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: '0px', marginBottom: 1}}
                    contentChildren={
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, rowGap: 5, flexWrap: 'wrap'}}>
                                <AMultiSelectFilter
                                    options={powerStationOptions}
                                    selectedIds={selectedPowerStationIds}
                                    onApply={handleApplyPowerStations}
                                    triggerLabel="Usinas"
                                    searchPlaceholder="Buscar usina..."
                                />
                                <DateTimePicker
                                    label="Data inicial"
                                    ampm={false}
                                    inputFormat="DD/MM/YYYY HH:mm"
                                    value={startDateInput}
                                    onChange={handleRangeStartChange}
                                    renderInput={(params: TextFieldProps) => <TextField
                                        {...params}
                                        error={dateRangeErrorField === 'start'}
                                        helperText={dateRangeErrorField === 'start' ? dateRangeError : undefined}
                                        FormHelperTextProps={dateHelperTextProps('start')}
                                        sx={dateFieldSx}
                                    />}
                                />
                                <DateTimePicker
                                    label="Data final"
                                    ampm={false}
                                    inputFormat="DD/MM/YYYY HH:mm"
                                    value={endDateInput}
                                    onChange={handleRangeEndChange}
                                    renderInput={(params: TextFieldProps) => <TextField
                                        {...params}
                                        error={dateRangeErrorField === 'end'}
                                        helperText={dateRangeErrorField === 'end' ? dateRangeError : undefined}
                                        FormHelperTextProps={dateHelperTextProps('end')}
                                        sx={dateFieldSx}
                                    />}
                                />
                            </Box>
                        </LocalizationProvider>
                    }
                />
            </Box>
            <ACard
                styleProps={{
                    headerStyle: {height: 0, margin: 0, padding: 0},
                    contentStyle: {padding: 0},
                    cardStyle: {borderRadius: "0 0 8px 8px", marginLeft: 1}
                }}
            >
                <Box sx={{ position: "relative" }}>
                <ADataGrid
                    ariaLabel={'alarm-audit-grid'}
                    hideFilters={false}
                    showFilterOptions={false}
                    hideSelection={true}
                    size={"small"}
                    headerStyle={{borderRadius: 0}}
                    tableContainerStyle={{borderRadius: 0}}
                    columns={columns}
                    rows={rows}
                    page={pagedSearchParams.page}
                    loading={isAlarmsLoading}
                    totalOfRecords={totalOfRecords}
                    onFiltersChange={handleOnFiltersChange}
                    rowsPerPage={pagedSearchParams.size}
                    actionColumnWidth={50}
                    maxHeight={700}
                />
                    <Box
                        sx={{
                            position: "absolute",
                            left: 16,
                            bottom: 6,
                            display: "flex",
                            gap: 1,
                            zIndex: 1,
                        }}
                    >
                        {isDownloadCsv ?
                            <LoadingButton
                                loading
                                loadingPosition="end"
                                endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                            >
                                Carregando..
                            </LoadingButton>
                            :
                            <Button
                                size="medium"
                                variant="text"
                                startIcon={<SimCardDownloadOutlined fontSize="large" />}
                                onClick={() => getOnClickByType("csv")}
                                disabled={!isAuthorizedToSearchByCsv || isExportDisabled}
                            >
                                CSV
                            </Button>}
                        {isDownloadExcel ?
                            <LoadingButton
                                loading
                                loadingPosition="end"
                                endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                            >
                                Carregando..
                            </LoadingButton>
                            :
                            <Button
                                size="medium"
                                variant="text"
                                startIcon={<SimCardDownloadOutlined fontSize="large" />}
                                onClick={() => getOnClickByType("excel")}
                                disabled={!isAuthorizedToSearchByExcel || isExportDisabled}
                            >
                                Excel
                            </Button>}
                    </Box>
                </Box>
                {rows.length === 0 && !isAlarmsLoading &&
                // TODO: Substituir Box customizado por componente EmptyState quando componente estiver disponível
                // Rastreador: SGD-3420
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            minHeight: '200px',
                            padding: 4,
                            backgroundColor: theme.palette.background.default,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            height: '377px',
                        }}
                    >
                        <Stack spacing={1.5} alignItems="center" textAlign="center">
                            <Box
                                sx={{
                                    fontSize: 40,
                                    display: 'flex',
                                    color: theme.palette.primary.main,
                                    mb: 0.5,
                                }}
                            >
                                <SearchOutlinedIcon />
                            </Box>

                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Alarmes não encontrados
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    maxWidth: '300px',
                                }}
                            >
                                Ajuste os critérios de busca utilizados e tente novamente.
                            </Typography>
                        </Stack>
                    </Box>
                }
            </ACard>
        </>
    )
}

export default function AuditAlarmsPage() {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <AuditAlarms/>
        </QueryClientProvider>
    )
}
